import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../app/store";
import { IOfferedSubjectData } from "../types";
import { setCarrearActual } from "./studentSlice";
import { doLoginGlobalFlagsBuilder } from "../reduxAsyncThunks/login.thunks";

interface ModalsOverlays {
  inTransitInscriptionRequet: boolean;
  panelInfoSubjectOffered: boolean;
  panelInfoSubjectInscribed: boolean;
  panelFilters: boolean;
  mobileFeatures: boolean;
  tabletDevice: boolean;
  collapseCards: boolean;
  collapseCardsSinCupo: boolean;
  lastCalendarAdd: number;
  desktopTab: "calendario" | "materias";
  sectionToChange: { id: number; idMateria: number; anioLectivo: number } | null;
  confirmChangeSectionData: IOfferedSubjectData | null;
  bannerPrioridadVisible: boolean;
  bannerCountdownVisible: boolean;
  lastOfertaUpdateTime: number | null;
  invalidarSemaforoSocketCount: number;
  isMobileSearchActive: boolean;
  isLogginProcess: boolean;
  hasRedirectedToCarrera: boolean;
  turnstileVerified: boolean;
  ofertaInvalidandoOVenciendo: boolean;
  isLoggingOut: boolean;
  loginCompleted: boolean;
  verFijadas: boolean;
}

// Primero agregamos una constante para la key del localStorage
const STORAGE_KEYS = {
  HAS_REDIRECTED_TO_CARRERA: 'hasRedirectedToCarrera'
} as const;

const initialState: ModalsOverlays = {
  inTransitInscriptionRequet: false,
  panelInfoSubjectOffered: false,
  panelInfoSubjectInscribed: false,
  panelFilters: false,
  mobileFeatures: false,
  tabletDevice: false,
  collapseCards: false,
  collapseCardsSinCupo: false,
  lastCalendarAdd: 0,
  desktopTab: "materias",
  sectionToChange: null,
  confirmChangeSectionData: null,
  bannerPrioridadVisible: true,
  bannerCountdownVisible: true,
  lastOfertaUpdateTime: null,
  invalidarSemaforoSocketCount: 0,
  isMobileSearchActive: false,
  isLogginProcess: false,
  hasRedirectedToCarrera: localStorage.getItem(STORAGE_KEYS.HAS_REDIRECTED_TO_CARRERA) === 'true',
  turnstileVerified: process.env.REACT_APP_ENV === 'Prd' ? false : true,
  ofertaInvalidandoOVenciendo: false,
  isLoggingOut: false,
  loginCompleted: !!localStorage.getItem("carreraActual"),
  verFijadas: false,
};

export const globalFlagsSlice = createSlice({
  name: "globalFlags",
  initialState,
  reducers: {
    setInTransitInscriptionRequest: (state, action) => {
      state.inTransitInscriptionRequet = action.payload;
    },
    setPanelSubjectOffered: (state, action) => {
      state.panelInfoSubjectOffered = action.payload;
    },
    setPanelSubjectInscribed: (state, action) => {
      state.panelInfoSubjectInscribed = action.payload;
    },
    setPanelFilters: (state, action) => {
      state.panelFilters = action.payload;
    },
    setMobileFeatures: (state, action) => {
      state.mobileFeatures = action.payload;
    },
    setTabletDevice: (state, action) => {
      state.tabletDevice = action.payload;
    },
    setCardsCollapse: (state, action) => {
      if (action.payload === "all") {
        state.collapseCards = true;
        state.collapseCardsSinCupo = false;
      } else if (action.payload === "sinCupo") {
        state.collapseCardsSinCupo = true;
        state.collapseCards = false;
      } else if (action.payload === "none") {
        state.collapseCardsSinCupo = false;
        state.collapseCards = false;
      }
    },
    setLastCalendarAdd: (state, action: { payload: number }) => {
      state.lastCalendarAdd = action.payload;
    },
    setDesktopTab: (state, action) => {
      state.desktopTab = action.payload;
    },
    setSectionToChange: (state, action: { payload: { id: number; idMateria: number; anioLectivo: number } | null }) => {
      state.sectionToChange = action.payload;
    },
    setConfirmChangeSectionData: (state, action) => {
      state.confirmChangeSectionData = action.payload;
    },
    setBannerPrioridadVisible: (state, action) => {
      state.bannerPrioridadVisible = action.payload;
    },
    setBannerCountdownVisible: (state, action) => {
      state.bannerCountdownVisible = action.payload;
    },
    setLastUpdateOferta: (state, action) => {
      state.lastOfertaUpdateTime = action.payload;
    },
    setInvalidarSemaforoSocketCount: (state, action) => {
      state.invalidarSemaforoSocketCount = action.payload;
    },
    setIsMobileSearchActive: (state, action) => {
      state.isMobileSearchActive = action.payload;
    },
    setIsLogginProcess: (state, action) => {
      state.isLogginProcess = action.payload;
    },
    setTurnstileVerified: (state, action) => {
      state.turnstileVerified = action.payload;
    },
    setHasRedirectedToCarrera: (state, action) => {
      state.hasRedirectedToCarrera = action.payload;
      localStorage.setItem(STORAGE_KEYS.HAS_REDIRECTED_TO_CARRERA, action.payload.toString());
    },
    setOfertaInvalidandoOVenciendo: (state, action) => {
      state.ofertaInvalidandoOVenciendo = action.payload;
    },
    setIsLoggingOut: (state, action) => {
      state.isLoggingOut = action.payload;
    },
    setLoginCompleted: (state, action) => {
      state.loginCompleted = action.payload;
    },
    setVerFijadas: (state, action) => {
      state.verFijadas = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setCarrearActual, (state, { payload }) => {
      if (typeof payload === "string" && payload.trim()) {
        state.loginCompleted = true;
      }
    });
    doLoginGlobalFlagsBuilder(builder);
  },
});

export const selectInTransitInRquest = (state: RootState) => state.globalFlags.inTransitInscriptionRequet;
export const selectPanelSubjectOffered = (state: RootState) => state.globalFlags.panelInfoSubjectOffered;
export const selectPanelSubjectInscribed = (state: RootState) => state.globalFlags.panelInfoSubjectInscribed;
export const selectPanelFilters = (state: RootState) => state.globalFlags.panelFilters;
export const selectMobileFeatures = (state: RootState) => state.globalFlags.mobileFeatures;
export const selectTabletDevice = (state: RootState) => state.globalFlags.tabletDevice;
export const selectFlags = (state: RootState) => state.globalFlags;
export const selectInvalidarSemaforoSocketCount = (state: RootState) => state.globalFlags.invalidarSemaforoSocketCount;
export const selectTurnstileVerified = (state: RootState) => state.globalFlags.turnstileVerified;
export const selectBannerCountdownVisible = (state: RootState) => state.globalFlags.bannerCountdownVisible;
export const selectOfertaInvalidandoOVenciendo = (state: RootState) => state.globalFlags.ofertaInvalidandoOVenciendo;
export const selectIsLoggingOut = (state: RootState) => state.globalFlags.isLoggingOut;
export const selectLoginCompleted = (state: RootState) => state.globalFlags.loginCompleted;
export const selectVerFijadas = (state: RootState) => state.globalFlags.verFijadas;

export const {
  setInTransitInscriptionRequest,
  setPanelSubjectOffered,
  setPanelSubjectInscribed,
  setPanelFilters,
  setMobileFeatures,
  setTabletDevice,
  setCardsCollapse,
  setLastCalendarAdd,
  setDesktopTab,
  setSectionToChange,
  setConfirmChangeSectionData,
  setBannerPrioridadVisible,
  setLastUpdateOferta,
  setInvalidarSemaforoSocketCount,
  setTurnstileVerified,
  setIsMobileSearchActive,
  setIsLogginProcess,
  setHasRedirectedToCarrera,
  setBannerCountdownVisible,
  setOfertaInvalidandoOVenciendo,
  setIsLoggingOut,
  setLoginCompleted,
  setVerFijadas,
} = globalFlagsSlice.actions;

export default globalFlagsSlice.reducer;
